import { stringifyMarkerStyle } from "@/lib/diff/markerType.ts"
import { MarkupBuilderImpl } from "@/lib/diff/markupBuilder.ts"
import { LineMarker, SortedList } from "@/lib/diff/misc.ts"
import { compareTo, isNotNull, mapNotNull } from "@/lib/std.ts"

import type { SyntaxMarkupTheme } from "@/components/Diff/diffColors.ts"
import type { InlineDiff } from "@/lib/client/generated/TypeDefinitions.ts"
import type { MarkerStyle, MarkerType } from "@/lib/diff/markerType.ts"
import type { MarkupBuilder } from "@/lib/diff/markupBuilder.ts"
import type { TextRangeImpl } from "@/lib/diff/misc.ts"
import type { Property } from "csstype"


const LINE_GUTTER_HORIZONTAL_PADDING_PX = 10
// This number is not set in stone, it depends on the font and may change when the font is updated
const DEFAULT_CHAR_WIDTH = 7.75
const MIN_LINE_GUTTER_SYMBOLS_COUNT = 2
const MIN_LINE_GUTTER_WIDTH = DEFAULT_CHAR_WIDTH * MIN_LINE_GUTTER_SYMBOLS_COUNT + LINE_GUTTER_HORIZONTAL_PADDING_PX * 2


export function buildLineMarkers(type: MarkerType, ranges: TextRangeImpl[]): LineMarker[] {
  return ranges.filter(it => it.isNotEmpty()).map(it => new LineMarker(type, it))
}

export function calcMaxLineNumber(diff: InlineDiff): number {
  const maxOldLine = mapNotNull(diff.lines, it => it.oldLineNum)
  const maxNewLine = mapNotNull(diff.lines, it => it.newLineNum)

  return maxOldLine.concat(maxNewLine).reduce((acc, it) => Math.max(acc, it || 0), 0)
}

function getLineNumberWidth(lineIndex: number, digitWidth: number): number {
  return digitWidth * getNumberLength(lineIndex)
}

function getNumberLength(number: number): number {
  return (number + 1).toString().length
}

export function getLineGutterWidth(diff: InlineDiff): Property.Width {
  return `${Math.max(
    MIN_LINE_GUTTER_WIDTH,
    getLineNumberWidth(calcMaxLineNumber(diff), DEFAULT_CHAR_WIDTH) * 2 + LINE_GUTTER_HORIZONTAL_PADDING_PX * 3
  )}px`
}

export class CodeRenderingOptions {
  constructor(
    readonly tabSize: number,
    readonly drawTab: boolean,
    readonly drawBidi: boolean,
  ) {
  }

  // Actual tab size at a given position
  spaces(position: number): number {
    return this.tabSize - (position % this.tabSize)
  }

  // Calculate line length with expanded tabs
  expandedLength(offset: number, lineText: string): number {
    let length = 0
    for (const char of lineText) {
      if (char === "\t") {
        length += this.spaces(offset + length)
      } else {
        length++
      }
    }

    return length
  }

  static FileTextDefault = new CodeRenderingOptions(4, false, true)
  static DiffDefault = new CodeRenderingOptions(4, true, true)
}

export const CodeRendering = {
  // https://www.unicode.org/reports/tr9/
  bidi: new Map<string, string>([
    [String.fromCharCode(0x202A), "LRE"],
    [String.fromCharCode(0x202B), "RLE"],
    [String.fromCharCode(0x202C), "PDF"],
    [String.fromCharCode(0x202D), "LRO"],
    [String.fromCharCode(0x202E), "RLO"],

    [String.fromCharCode(0x200E), "LRM"],
    [String.fromCharCode(0x200F), "RLM"],

    [String.fromCharCode(0x2066), "LRI"],
    [String.fromCharCode(0x2067), "RLI"],
    [String.fromCharCode(0x2068), "FSI"],
    [String.fromCharCode(0x2069), "PDI"],

    [String.fromCharCode(0x061C), "ALM"],
  ])
}

export function renderCodeFragment(builder: MarkupBuilder, text: string, offset: number, options: CodeRenderingOptions): void {
  const takeWhile = (from: number, predicate: (char: string) => boolean): number => {
    let next = from + 1
    while (next < text.length && predicate(text[next])) {
      next++
    }

    return next
  }

  let start = 0
  let visualPosition = offset

  while (start < text.length) {
    const ch = text[start]

    if (CodeRendering.bidi.get(ch) !== undefined) {
      if (options.drawBidi) {
        const bidi = CodeRendering.bidi.get(ch) || (() => {
          throw new Error("Not a BiDi character")
        })()

        // Put visual definition of control character before it
        builder.span(it => {
          // LRI (name) PDI
          it.attr("style", "color: var(--secondary-text-color); border-width: 1px; border-style: solid; font-weight: normal")
          it.text(`${String.fromCharCode(0x2066)}${bidi}${String.fromCharCode(0x2069)}`)
        })
      }

      builder.text(ch)

      visualPosition++
      start++
    } else if (ch === "\t") {
      const spaces = options.spaces(visualPosition)
      assert(spaces > 0)

      if (options.drawTab) {
        builder.span(it => {
          it.attr("style", "color: var(--secondary-text-color); font-weight: normal")
          it.text("".padStart(spaces - 1, String.fromCharCode(0x2014)) + String.fromCharCode(0x2794))
        })
      } else {
        builder.text(" ".repeat(spaces))
      }

      visualPosition += spaces
      start++
    } else if (isAlpha(ch) || ch === "_") {
      const next = takeWhile(start, (char) => isAlphaNumeric(char) || char === "_")
      const word = text.substring(start, next)

      builder.text(word)

      visualPosition += (next - start)
      start = next
    } else {
      const next = takeWhile(start, (char) => CodeRendering.bidi.get(char) === undefined && char !== "\t" && !isAlpha(char) && char !== "_")
      builder.text(text.substring(start, next))

      visualPosition += (next - start)
      start = next
    }
  }
}


abstract class RangeRender {
  abstract render(builder: MarkupBuilder): void

  abstract isEmpty: boolean
}

export class Tag extends RangeRender {
  private innerContents: RangeRender[] = []

  constructor(
    readonly markerStyle: MarkerStyle | undefined | null,
    readonly overridingMarkers: MarkerStyle[] = []
  ) {
    super()
  }

  render(builder: MarkupBuilder) {
    builder.span(this.tagBuilder)
  }

  addInnerContent(invocable: RangeRender) {
    this.innerContents.push(invocable)
  }

  get isEmpty() {
    return this.innerContents.length === 0 || this.innerContents.every(it => it.isEmpty)
  }

  private tagBuilder = (builder: MarkupBuilder) => {
    const markerStyle = Object.assign({}, this.markerStyle, ...this.overridingMarkers)
    builder.attr("style", stringifyMarkerStyle(markerStyle))

    for (const invocable of this.innerContents) {
      invocable.render(builder)
    }
  }
}

export class Text extends RangeRender {
  constructor(readonly text: string, readonly offset: number, readonly options: CodeRenderingOptions) {
    super()
  }

  render(builder: MarkupBuilder): void {
    renderCodeFragment(builder, this.text, this.offset, this.options)
  }

  readonly isEmpty = false
}

function isAlpha(ch: string): boolean {
  return (ch >= "a" && ch <= "z") || (ch >= "A" && ch <= "Z")
}

function isAlphaNumeric(ch: string): boolean {
  return isAlpha(ch) || (ch >= "0" && ch <= "9")
}

class Marker {
  private _offset: number
  pair: Marker | null = null

  constructor(readonly lineMarker: LineMarker, readonly isOpening: boolean, offset: number) {
    this._offset = offset
  }

  get offset() {
    return this._offset
  }

  withChangedOffset(offset: number): Marker {
    assert(compareTo(this.pair!.offset, offset) === (this.isOpening ? 1 : -1))
    this._offset = offset

    return this
  }

  compareTo(other: Marker): number {
    if (other === this.pair) {
      return this.isOpening ? -1 : 1
    }

    if (this.offset !== other.offset) {
      return this.offset - other.offset
    }

    if (this.isOpening !== other.isOpening) {
      return this.isOpening ? 1 : -1
    }

    const pairsOffsetsComparing = this.pair!.offset - other.pair!.offset
    if (pairsOffsetsComparing !== 0) {
      return -pairsOffsetsComparing
    }

    return (this.isOpening ? 1 : -1) * this.type.compareTo(other.type)
  }

  static bundCouple(first: Marker, second: Marker) {
    assert(first.isOpening !== second.isOpening, "Markers must have opposite types")

    if (first.isOpening) {
      assert(first.offset < second.offset, "First marker must be before the second")
    } else {
      assert(first.offset > second.offset, "First marker must be after the second")
    }

    first.pair = second
    second.pair = first
  }

  get type() {
    return this.lineMarker.type
  }
}

type RenderLineWithMarkersParams = {
  lineText: string
  lineMarkers: LineMarker[]
  syntaxTheme: SyntaxMarkupTheme
  syntaxMarkupOffsetLimit?: number | null
  options: CodeRenderingOptions
}

export function renderLineWithMarkers(params: RenderLineWithMarkersParams) {
  const lineText = params.lineText
  const lineMarkers = params.lineMarkers
  const syntaxTheme = params.syntaxTheme
  const syntaxMarkupOffsetLimit = params.syntaxMarkupOffsetLimit || null
  const options = params.options

  const markers = lineMarkers
    .filter(it => syntaxMarkupOffsetLimit === null || it.range.start < syntaxMarkupOffsetLimit || !it.type.syntax)
    .flatMap(it => {
      const openingMarker = new Marker(it, true, it.range.start)
      const closingMarker = new Marker(it, false, it.range.start + it.range.length)
      Marker.bundCouple(openingMarker, closingMarker)

      return [openingMarker, closingMarker]
    })
    .sort((a, b) => a.offset - b.offset)

  const substring = (start: number, end: number): string => {
    return lineText.substring(start, Math.min(end, lineText.length))
  }

  const appendText = (builder: MarkupBuilder, text: string, offer: number) => {
    renderCodeFragment(builder, text, offer, options)
  }

  const builder = new MarkupBuilderImpl(document.createElement("div"))

  const markersQueue = new SortedList<Marker>()
  markersQueue.addAll(markers)

  const openedRanges: [Marker, Tag][] = []

  let visualPos = 0
  let currentPos = 0

  while (!markersQueue.isEmpty()) {
    const marker = markersQueue.remove()
    if (!(marker.offset >= currentPos && marker.offset <= lineText.length)) {
      break
    }

    if (currentPos < marker.offset) {
      const fragment = substring(currentPos, marker.offset)
      if (openedRanges.length === 0) {
        // No styles, just put span
        builder.span(it => appendText(it, fragment, visualPos))
      } else {
        const tag = openedRanges[openedRanges.length - 1][1]
        tag.addInnerContent(new Text(fragment, visualPos, options))
      }

      visualPos += options.expandedLength(visualPos, fragment)
    }

    // Processing marker
    if (marker.isOpening) {
      const overridingMarkers = openedRanges
        .filter(it => it[0].type.priority <= marker.type.priority)
        .map(it => it[1].markerStyle)
        .filter(isNotNull)

      const render = new Tag(marker.type.styles?.(syntaxTheme), overridingMarkers)

      if (openedRanges.length > 0) {
        openedRanges[openedRanges.length - 1][1].addInnerContent(render)
      }

      openedRanges.push([marker, render])
    } else {
      let lastOpened = openedRanges.pop()
      while (lastOpened && lastOpened[0].compareTo(marker.pair!) !== 0) {
        markersQueue.add(lastOpened[0].withChangedOffset(marker.offset))
        if (openedRanges.length === 0) break // This should never happen
        lastOpened = openedRanges.pop()
      }

      if (openedRanges.length === 0 && lastOpened) {
        lastOpened[1].render(builder)
      }
    }

    currentPos = marker.offset
  }

  // Writing the rest of the line
  if (currentPos < lineText.length) {
    builder.span(it => appendText(it, substring(currentPos, lineText.length), visualPos))
  }

  return builder.renderToString()
}

function assert(condition: boolean, message: string = "Assertion failed") {
  if (!condition) {
    throw new Error(message)
  }
}